import { useMemo, useState } from "react";
import { Alert, Button, Col, Table } from "react-bootstrap";
import { useGetPayments, useCreatePayment, useDeletePayment, useUpdatePayment } from "../../../../../api/fleet-management/payments/payments.hooks";
import {
  getFleetMediaUrl,
} from "../../../../../api/fleet-management/api";
import {
  formatBookingAmount,
  formatBookingDateTime,
} from "../utils/booking.utils";
import DeleteModal from "../../../../components/DeleteModal";
import PaymentFormModal from "./PaymentFormModal";
import { MdDelete, MdEdit } from "react-icons/md";

const getTodayDateValue = () => new Date().toISOString().slice(0, 10);

const initialPaymentForm = {
  paymentType: "",
  mode: "",
  amount: "",
  paymentDate: getTodayDateValue(),
  description: "",
  receipt: "",
  transactionId: "",
};

const normalizePayments = (data) => {
  if (Array.isArray(data)) return data;
  return data?.payments || data?.items || [];
};

const normalizePaymentType = (payment) => {
  const type = (payment?.paymentType || payment?.type || "").toLowerCase();
  if (type === "advance") return "Advance";
  if (type === "remaining") return "Remaining";
  if (type === "refund") return "Refund";
  return "-";
};

const normalizePaymentMode = (payment) => {
  const mode = (payment?.mode || "").toLowerCase();
  if (mode === "online") return "Online";
  if (mode === "cash") return "Cash";
  if (mode === "direct-bank-transfer") return "Direct Bank Transfer";
  return "-";
};

const getPaymentIdValue = (payment) => {
  if (!payment) return "";
  if (typeof payment === "string") return payment;
  return payment?._id || payment?.id || "";
};

const mapPaymentToForm = (payment) => ({
  paymentType: payment?.paymentType || payment?.type || "",
  mode: payment?.mode || "",
  amount:
    payment?.amount !== undefined && payment?.amount !== null
      ? String(payment.amount)
      : "",
  paymentDate: payment?.paymentDate
    ? new Date(payment.paymentDate).toISOString().slice(0, 10)
    : getTodayDateValue(),
  description: payment?.description || "",
  receipt: payment?.receipt || "",
  transactionId: payment?.transactionId || "",
});


const BookingDetailsPaymentsSection = ({ bookingId }) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState("");
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [form, setForm] = useState(initialPaymentForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const { data: paymentsData, isLoading, isError, error } = useGetPayments(
    bookingId ? { bookingId } : null
  );
  const { mutate: createPayment, isLoading: isCreating } = useCreatePayment();
  const { mutate: updatePayment, isLoading: isUpdating } = useUpdatePayment();
  const { mutate: deletePayment, isLoading: isDeleting } = useDeletePayment();

  const payments = useMemo(() => normalizePayments(paymentsData), [paymentsData]);
  const isSubmitting = isCreating || isUpdating;

  const openCreateModal = () => {
    setFeedback({ type: "", message: "" });
    setValidationErrors({});
    setEditingPaymentId("");
    setForm({ ...initialPaymentForm, paymentDate: getTodayDateValue() });
    setShowPaymentModal(true);
  };

  const openEditModal = (payment) => {
    const paymentId = getPaymentIdValue(payment);
    if (!paymentId) return;

    setFeedback({ type: "", message: "" });
    setValidationErrors({});
    setEditingPaymentId(paymentId);
    setForm(mapPaymentToForm(payment));
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    if (isSubmitting) return;
    setShowPaymentModal(false);
    setEditingPaymentId("");
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validatePaymentForm = () => {
    const nextErrors = {};

    if (!form.paymentType) nextErrors.paymentType = "Payment type is required.";
    if (!form.mode) nextErrors.mode = "Mode is required.";
    if (form.amount === "" || Number(form.amount) <= 0) nextErrors.amount = "Amount must be greater than 0.";
    if (!form.paymentDate) nextErrors.paymentDate = "Payment date is required.";
    if (!form.description.trim()) nextErrors.description = "Description is required.";

    const needsTransactionId = form.mode === "online" || form.mode === "direct-bank-transfer";
    if (needsTransactionId && !form.transactionId.trim()) {
      nextErrors.transactionId = "Transaction ID is required for online or bank transfer mode.";
    }

    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmitPayment = (e) => {
    e.preventDefault();
    setFeedback({ type: "", message: "" });

    if (!bookingId || !validatePaymentForm()) return;

    const payload = {
      bookingId,
      paymentType: form.paymentType,
      mode: form.mode,
      amount: Number(form.amount),
      paymentDate: form.paymentDate,
      description: form.description.trim(),
      receipt: form.receipt || "",
      transactionId: form.transactionId.trim() || undefined,
      status: "completed",
    };

    if (editingPaymentId) {
      updatePayment(
        { paymentId: editingPaymentId, data: payload },
        {
          onSuccess: () => {
            setFeedback({ type: "success", message: "Payment updated successfully." });
            setShowPaymentModal(false);
            setEditingPaymentId("");
            setForm(initialPaymentForm);
          },
          onError: (updateError) => {
            setFeedback({
              type: "danger",
              message: updateError?.response?.data?.message || "Failed to update payment.",
            });
          },
        }
      );
      return;
    }

    createPayment(payload, {
      onSuccess: () => {
        setFeedback({ type: "success", message: "Payment created successfully." });
        setShowPaymentModal(false);
        setForm(initialPaymentForm);
      },
      onError: (createError) => {
        setFeedback({
          type: "danger",
          message: createError?.response?.data?.message || "Failed to create payment.",
        });
      },
    });
  };

  const openDeleteModal = (payment) => {
    const paymentId = getPaymentIdValue(payment);
    if (!paymentId || isDeleting) return;
    setSelectedPayment(payment);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setSelectedPayment(null);
  };

  const handleDeletePayment = async () => {
    const paymentId = getPaymentIdValue(selectedPayment);
    if (!paymentId) return;

    setFeedback({ type: "", message: "" });

    deletePayment(
      { paymentId },
      {
        onSuccess: () => {
          setFeedback({ type: "success", message: "Payment deleted successfully." });
          closeDeleteModal();
        },
        onError: (deleteError) => {
          setFeedback({
            type: "danger",
            message: deleteError?.response?.data?.message || "Failed to delete payment.",
          });
        },
      }
    );
  };

  return (
    <Col lg={12}>
      <div className="card">
        <div className="card-header border-0 pb-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Payments</h5>
          <Button size="sm" onClick={openCreateModal} disabled={!bookingId || isDeleting || isSubmitting}>
            Add Payment
          </Button>
        </div>
        <div className="card-body pt-3">
          {feedback.message && (
            <Alert variant={feedback.type} className="mb-3">
              {feedback.message}
            </Alert>
          )}

          {isLoading ? (
            <div className="text-muted">Loading payments...</div>
          ) : null}

          {isError ? (
            <Alert variant="danger" className="mb-0">
              {error?.response?.data?.message || "Failed to load payments."}
            </Alert>
          ) : null}

          {!isLoading && !isError && payments.length === 0 ? (
            <div className="text-muted">No payment records available.</div>
          ) : null}

          {!isLoading && !isError && payments.length > 0 ? (
            <div className="table-responsive">
              <Table bordered hover size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Payment Type</th>
                    <th>Mode</th>
                    <th>Amount</th>
                    <th>Time & Date</th>
                    <th>Status</th>
                    <th>Reference</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => {
                    const paymentType = normalizePaymentType(payment);
                    const paymentId = getPaymentIdValue(payment);

                    return (
                      <tr key={payment?._id || `${payment?.bookingId}-${payment?.createdAt}`}>
                        <td>{paymentType}</td>
                        <td>{normalizePaymentMode(payment)}</td>
                        <td>{formatBookingAmount(payment?.amount)}</td>
                        <td>{formatBookingDateTime(payment?.paymentDate || payment?.createdAt)}</td>
                        <td>{payment?.status || "-"}</td>
                        <td>
                          {payment?.transactionId ? (
                            payment.transactionId
                          ) : payment?.receipt ? (
                            <a
                              href={getFleetMediaUrl(payment.receipt)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Download receipt"
                              aria-label="Download receipt"
                              className="text-primary"
                            >
                              <i className="bi bi-download fs-5" />
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => openEditModal(payment)}
                            disabled={isDeleting || isSubmitting || !paymentId}
                          >
                            <MdEdit size={18} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => openDeleteModal(payment)}
                            disabled={isDeleting || isSubmitting || !paymentId}
                          >
                            <MdDelete size={18} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : null}
        </div>
      </div>

      <PaymentFormModal
        show={showPaymentModal}
        isEditMode={Boolean(editingPaymentId)}
        form={form}
        errors={validationErrors}
        isSubmitting={isSubmitting}
        onClose={closePaymentModal}
        onSubmit={handleSubmitPayment}
        onFieldChange={updateField}
      />

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDeletePayment}
        title="Delete Payment"
        message="Are you sure you want to delete this payment? This action cannot be undone."
        confirmText="Delete Payment"
        isLoading={isDeleting}
      />
    </Col>
  );
};

export default BookingDetailsPaymentsSection;